import React, { useEffect, useState, useCallback, useRef } from 'react';
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import { MapPin, Search, Loader2, Navigation, X, SlidersHorizontal, Info, Sparkles, Compass, ChevronDown } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import OutletLets from './OutletLets';
import CafePage from './CafePage';
import Nav from './common/Nav';
import BottomNav from './BottomNav';
import FilterSheet from './FilterSheet';
import FoodSearch from './FoodSearch';
import WelcomeModal, { shouldShowWelcome } from './WelcomeModal';
import { host } from './script/variables';
import {
  PLACEHOLDER_PLACES, QUICK_FILTERS,
  AMBIENCE_FILTERS, VIBE_FILTERS, CUISINE_CATS, OCCASION_FILTERS,
} from './filterData.js';
import './style/Home.css';
// import './style/Home.overrides.css';

/* ── Blog card ── */
const BlogCard = ({ blog }) => (
  <Link to={`/blog/${blog.title}`} className="hm-blog-card">
    <div className="hm-blog-img">
      {blog.thumbnail && blog.thumbnail !== '#'
        ? <img src={blog.thumbnail} alt={blog.title} className="hm-blog-thumb" />
        : <span className="hm-blog-fallback-emoji">📝</span>
      }
    </div>
    <div className="hm-blog-info">
      {blog.tags?.length > 0 && (
        <div className="hm-blog-cat">{blog.tags[0]?.value ?? blog.tags[0]}</div>
      )}
      <div className="hm-blog-title">{blog.title}</div>
      <div className="hm-blog-meta">
        {blog.createdAt && <>🕐 {new Date(blog.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} · </>}
        {blog.author?.name && <>By {blog.author.name}</>}
      </div>
    </div>
  </Link>
);

const useTypingPlaceholder = (places, typingSpeed = 80, pauseDuration = 1500) => {
  const [placeholder, setPlaceholder] = useState('');

  useEffect(() => {
    let placeIndex = 0;
    let charIndex = 0;
    let isDeleting = false;
    let timer;

    const tick = () => {
      const currentPlace = places[placeIndex];
      if (!isDeleting) {
        setPlaceholder(currentPlace.slice(0, charIndex + 1));
        charIndex++;
        if (charIndex === currentPlace.length) {
          isDeleting = true;
          timer = setTimeout(tick, pauseDuration);
          return;
        }
      } else {
        setPlaceholder(currentPlace.slice(0, charIndex - 1));
        charIndex--;
        if (charIndex === 0) {
          isDeleting = false;
          placeIndex = (placeIndex + 1) % places.length;
        }
      }
      timer = setTimeout(tick, isDeleting ? typingSpeed / 2 : typingSpeed);
    };

    timer = setTimeout(tick, typingSpeed);
    return () => clearTimeout(timer);
  }, [places, typingSpeed, pauseDuration]);

  return placeholder;
};

const BlogSkeleton = () => (
  <div className="hm-blogs-scroll">
    {Array.from({ length: 4 }).map((_, i) => (
      <div key={i} className="hm-blog-card hm-blog-card--skeleton">
        <div className="hm-sk hm-sk-img" />
        <div className="hm-sk-body">
          <div className="hm-sk hm-sk-tag" />
          <div className="hm-sk hm-sk-title" />
          <div className="hm-sk hm-sk-meta" />
        </div>
      </div>
    ))}
  </div>
);

const MomentCard = ({ moment }) => (
  <Link to="/happymoments" className="hm-moment-card">
    <div className="hm-moment-img-wrap">
      <img src={moment.imageurl} alt={moment.caption} className="hm-moment-img" />
      <div className="hm-moment-overlay">
        <div className="hm-moment-caption">{moment.caption}</div>
        <div className="hm-moment-likes">❤️ {moment.likes}</div>
      </div>
    </div>
    <div className="hm-moment-info">
      <div className="hm-moment-user">
        <img
          src={moment.user?.image}
          alt={moment.user?.displayName}
          className="hm-moment-avatar"
          onError={e => { e.target.style.display = 'none'; }}
        />
        <div className="hm-moment-user-meta">
          <span className="hm-moment-username">@{moment.user?.username}</span>
          <span className="hm-moment-cafe">📍 {moment.cafe?.name}</span>
        </div>
      </div>
    </div>
  </Link>
);

const MomentsSkeleton = () => (
  <div className="hm-moments-scroll">
    {Array.from({ length: 4 }).map((_, i) => (
      <div key={i} className="hm-moment-card hm-moment-card--skeleton">
        <div className="hm-sk hm-sk-moment-img" />
        <div className="hm-sk-body">
          <div className="hm-sk hm-sk-tag" />
          <div className="hm-sk hm-sk-meta" />
        </div>
      </div>
    ))}
  </div>
);

/* ─────────────────────────────────────────────────────────────── */

const Home = () => {
  /* location */
  const [coordinates, setCoordinates] = useState({ lat: null, lng: null });
  const [address, setAddress] = useState('');
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [showLocationPopup, setShowLocationPopup] = useState(false);
  const [showLocationSheet, setShowLocationSheet] = useState(false);

  /* outlets */
  const [nearestOutlets, setNearestOutlets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSingleItem, setIsSingleItem] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);

  /* filters — everything now lives behind one FilterSheet modal */
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const [vegMode, setVegMode] = useState(false);
  const [isOpen, setIsOpen] = useState(true);
  const [cuisine, setCuisine] = useState([]);
  const [q, setQ] = useState('');                 // free-text search (dish, name, cuisine, vibe, area)
  const [searchInfo, setSearchInfo] = useState(null);   // mode, notice, didYouMean, query, total
  const [searchError, setSearchError] = useState('');
  const [activeQF, setActiveQF] = useState(null);
  const [minRating, setMinRating] = useState(null);
  const [maxDistance, setMaxDistance] = useState(null); // null = "Any"

  const [activeAmbience, setActiveAmbience] = useState([]);
  const [activeVibes, setActiveVibes] = useState([]);
  const [activeCuisine, setActiveCuisine] = useState([]);
  const [activeOccasion, setActiveOccasion] = useState([]);

  /* blogs */
  const [homeBlogs, setHomeBlogs] = useState([]);
  const [blogsLoading, setBlogsLoading] = useState(false);
  const [blogsError, setBlogsError] = useState('');

  /* moments */
  const [homeMoments, setHomeMoments] = useState([]);
  const [momentsLoading, setMomentsLoading] = useState(false);
  const [momentsError, setMomentsError] = useState('');

  /* misc */
  const [isLogin, setIsLogin] = useState(false);

  /* first-visit intro — gated by localStorage, see WelcomeModal.js */
  const [showWelcome, setShowWelcome] = useState(false);
  const heroRef = useRef(null);

  useEffect(() => { setShowWelcome(shouldShowWelcome()); }, []);

  /* The intro's CTA drops the user straight into the search bar.
     PlacesAutocomplete owns that input, so it's reached through the
     hero rather than a ref we can hand to it. */
  const focusSearch = useCallback(() => {
    const hero = heroRef.current;
    if (!hero) return;
    hero.scrollIntoView({ behavior: 'smooth', block: 'start' });
    /* Wait out the smooth scroll — focusing mid-scroll cancels it. */
    setTimeout(() => hero.querySelector('.fsr-input')?.focus(), 420);
  }, []);

  const navigate = useNavigate();
  const openRestaurant = (id) => { setIsSingleItem(true); navigate(`/moredetails?cafeid=${id}`); };

  const activeFilterCount = [
    vegMode, isOpen, minRating != null, maxDistance != null,
    ...cuisine,
    ...activeAmbience, ...activeVibes,
    ...activeCuisine, ...activeOccasion,
  ].filter(Boolean).length;

  /* ── location ── */
  const fetchUserLocation = (forceRefresh = false) => {
    if (!forceRefresh) {
      const cached = localStorage.getItem('userCoordinates');
      if (cached) {
        try {
          const { lat, lng, address: cachedAddress } = JSON.parse(cached);
          setCoordinates({ lat, lng });
          if (cachedAddress) setAddress(cachedAddress);
          return;
        } catch (e) { /* ignore parse errors */ }
      }
    }

    if (!navigator.geolocation) {
      setLocationError('Geolocation not supported by your browser.');
      return;
    }

    setLocationLoading(true);
    setLocationError('');

    navigator.geolocation.getCurrentPosition(
      pos => {
        const { latitude: lat, longitude: lng } = pos.coords;
        setCoordinates({ lat, lng });
        setLocationLoading(false);
        localStorage.setItem('userCoordinates', JSON.stringify({ lat, lng, address: '' }));
      },
      (err) => {
        setLocationLoading(false);
        if (err.code === err.PERMISSION_DENIED) {
          setLocationError('PERMISSION_DENIED');
          setShowLocationPopup(true);
        } else {
          setLocationError('Unable to get your location. Please search manually.');
        }
      }
    );
  };

  const handleSelect = async value => {
    try {
      setLocationLoading(true);
      const result = await geocodeByAddress(value);
      const ll = await getLatLng(result[0]);
      setAddress(value);
      setCoordinates(ll);
      setShowLocationSheet(false);
      localStorage.setItem('userCoordinates', JSON.stringify({ lat: ll.lat, lng: ll.lng, address: value }));
    } catch (e) { console.error(e); }
    finally { setLocationLoading(false); }
  };

  /* ── quick filter row (under hero) ── */
  const handleQuickFilter = (qf) => {
    if (qf.cuisineVal === null && !qf.isVeg) {
      setActiveQF(null); setVegMode(false); setCuisine([]); return;
    }
    setActiveQF(qf.label);
    if (qf.isVeg) { setVegMode(true); setCuisine([]); }
    else { setVegMode(false); setCuisine([{ value: qf.cuisineVal, label: qf.label }]); }
  };

  /* ── toggle helpers (passed down into FilterSheet) ── */
  const toggleAmbience = (val) => setActiveAmbience(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
  const toggleVibe     = (val) => setActiveVibes(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
  const toggleCuisine  = (val) => setActiveCuisine(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
  const toggleOccasion = (val) => setActiveOccasion(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);

  const clearAllFilters = () => {
    setVegMode(false); setIsOpen(false); setActiveQF(null);
    setCuisine([]);
    setMinRating(null); setMaxDistance(null);
    setActiveAmbience([]); setActiveVibes([]); setActiveCuisine([]); setActiveOccasion([]);
  };

  /* ── search request (POST /outlet/search — never returns an empty page when places exist) ── */
  const buildPayload = (pg) => ({
    location: coordinates,
    q,
    page: pg,
    source: 'web',
    filters: {
      vegMode, isOpen, minRating, maxDistance,
      ambience: activeAmbience, vibes: activeVibes, cuisines: activeCuisine, occasions: activeOccasion,
      cuisine,
    },
  });

  const requestSearch = async (pg) => {
    const res = await fetch(`${host}/outlet/search`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildPayload(pg)),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(res.status === 429
        ? 'Too many searches in a moment — please wait a few seconds and try again.'
        : data.msg || 'Search is having trouble right now. Please try again.');
    }
    return data;
  };

  const searchSeq = useRef(0);
  const findNearestOutlets = useCallback(async (reset = false) => {
    if (coordinates.lat == null) return;
    const my = ++searchSeq.current;
    const pg = reset ? 1 : page;
    setIsLoading(true); setSearchError('');
    try {
      const data = await requestSearch(pg);
      if (my !== searchSeq.current) return;
      setNearestOutlets(data.results || []);
      setSearchInfo({ mode: data.mode, notice: data.notice, didYouMean: data.didYouMean || [], query: data.query, total: data.total });
      setHasMore(!!data.hasMore);
      setPage(reset ? 2 : pg + 1);
    } catch (e) {
      if (my === searchSeq.current) setSearchError(e.message);
    } finally {
      if (my === searchSeq.current) setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coordinates, page, q, maxDistance, minRating, cuisine, vegMode, isOpen, activeAmbience, activeVibes, activeCuisine, activeOccasion]);

  const loadingMore = useRef(false);
  const fetchMore = async () => {
    if (!coordinates.lat || !hasMore || isLoading || loadingMore.current) return;
    loadingMore.current = true;
    const my = searchSeq.current;
    try {
      const data = await requestSearch(page);
      if (my !== searchSeq.current) return;
      const list = data.results || [];
      setHasMore(!!data.hasMore);
      setPage((p) => p + 1);
      setNearestOutlets((prev) => [...prev, ...list.filter((c) => !prev.some((x) => x._id === c._id))]);
    } catch (e) {
      console.error(e);
      setHasMore(false);
    } finally {
      loadingMore.current = false;
    }
  };

  /* remove one interpreted part of the query ("rooftop") — whole words only */
  const removePhrase = (phrase) => {
    const words = String(phrase).toLowerCase().split(/\s+/).filter(Boolean);
    const tokens = q.split(/\s+/);
    for (let i = 0; i <= tokens.length - words.length; i++) {
      const slice = tokens.slice(i, i + words.length).map((x) => x.toLowerCase().replace(/[^\p{L}\p{N}]/gu, ''));
      if (slice.join(' ') === words.join(' ')) {
        tokens.splice(i, words.length);
        if (tokens[i - 1] && /^(in|at|near)$/i.test(tokens[i - 1])) tokens.splice(i - 1, 1);
        break;
      }
    }
    setQ(tokens.join(' ').trim());
  };

  /* ── blogs fetch ── */
  const fetchHomeBlogs = async () => {
    setBlogsLoading(true); setBlogsError('');
    try {
      const res = await fetch(`${host}/blog/fetchwithinp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ searchText: '', pageNo: 1 }),
      });
      if (!res.ok) throw new Error('fetch failed');
      const data = await res.json();
      setHomeBlogs(data.slice(0, 5));
    } catch { setBlogsError('Could not load posts.'); }
    finally { setBlogsLoading(false); }
  };

  /* ── moments fetch ── */
  const fetchHomeMoments = async () => {
    setMomentsLoading(true); setMomentsError('');
    try {
      const res = await fetch(`${host}/api/moments/homeMoments`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error('fetch failed');
      const data = await res.json();
      setHomeMoments(data.data?.slice(0, 6) ?? []);
    } catch { setMomentsError('Could not load moments.'); }
    finally { setMomentsLoading(false); }
  };

  const checkLogin = async () => {
    try {
      const res = await fetch(`${host}/user/vlogin`, {
        method: 'GET', headers: { 'Content-Type': 'application/json', Authorization: localStorage.getItem('token') },
      });
      setIsLogin(res.ok);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchUserLocation(); checkLogin(); fetchHomeBlogs(); fetchHomeMoments(); }, []);
  useEffect(() => { if (coordinates.lat && coordinates.lng) { setPage(1); findNearestOutlets(true); } }, [coordinates.lat, coordinates.lng]);

  useEffect(() => {
    if (coordinates.lat == null) return;
    const t = setTimeout(() => { setPage(1); findNearestOutlets(true); }, 300);
    return () => clearTimeout(t);
  }, [q, vegMode, isOpen, minRating, maxDistance, cuisine, activeAmbience, activeVibes, activeCuisine, activeOccasion]);

  const removeAmbience = (val) => setActiveAmbience(prev => prev.filter(v => v !== val));
  const removeVibe     = (val) => setActiveVibes(prev => prev.filter(v => v !== val));
  const removeCuisine  = (val) => setActiveCuisine(prev => prev.filter(v => v !== val));
  const removeOccasion = (val) => setActiveOccasion(prev => prev.filter(v => v !== val));

  const typingPlaceholder = useTypingPlaceholder(PLACEHOLDER_PLACES);

  const hasAnyActiveChip = vegMode || isOpen || minRating != null || maxDistance != null
    || activeAmbience.length > 0 || activeVibes.length > 0 || activeCuisine.length > 0 || activeOccasion.length > 0;

  /* ── render ── */
  return (
    <div className="hm-root">
      <Nav setIsLogin={setIsLogin} isLogin={isLogin} vegMode={vegMode} />

      {!isSingleItem ? (
        <div className="hm-content">

          {/* ───── COMPACT HERO ───── */}
          <section className="hm-hero hm-hero--compact" ref={heroRef}>
            <h1 className="hm-hero-title">Find Great Food<br />Near You</h1>
            <p className="hm-hero-sub">Search any dish, restaurant, cuisine or vibe — we’ll find it nearby</p>

            {/* Where we search — tap to change (same as the app) */}
            <button type="button" className="hm-loc-line" onClick={() => setShowLocationSheet(true)}>
              {locationLoading ? <Loader2 size={15} className="hm-spin" /> : <MapPin size={15} />}
              <span className="hm-loc-line-label">Restaurants near</span>
              <strong className="hm-loc-line-place">{locationLoading ? 'finding you…' : address || 'your current location'}</strong>
              <ChevronDown size={15} />
            </button>

            {/* One search box for everything */}
            <FoodSearch value={q} onSubmit={setQ} coords={coordinates} onOpenRestaurant={openRestaurant} />

            {locationError && locationError !== 'PERMISSION_DENIED' && (
              <p className="hm-location-error">⚠️ {locationError}</p>
            )}
          </section>

          {/* ───── QUICK FILTER CHIPS (trimmed, single row) ───── */}
          <div className="hm-section hm-section--tight">
            <div className="hm-quick-filters">
              <button
                type="button"
                className={`hm-qf-chip ${activeFilterCount > 0 ? 'hm-qf-chip--active' : ''}`}
                onClick={() => setShowFilterSheet(true)}
              >
                <SlidersHorizontal size={14} />
                Filters{activeFilterCount > 0 ? ` · ${activeFilterCount}` : ''}
              </button>
              {QUICK_FILTERS.map(qf => (
                <button
                  key={qf.label}
                  className={`hm-qf-chip ${activeQF === qf.label || (qf.cuisineVal === null && !qf.isVeg && activeQF === null) ? 'hm-qf-chip--active' : ''}`}
                  onClick={() => handleQuickFilter(qf)}
                >
                  <span className="hm-qf-emoji">{qf.emoji}</span>
                  {qf.label}
                </button>
              ))}
              <button type="button" className={`hm-qf-chip ${isOpen ? 'hm-qf-chip--active' : ''}`} onClick={() => setIsOpen((v) => !v)}>
                <span className="hm-qf-emoji">🕒</span>Open now
              </button>
              <button type="button" className={`hm-qf-chip ${minRating === 4 ? 'hm-qf-chip--active' : ''}`} onClick={() => setMinRating((v) => (v === 4 ? null : 4))}>
                <span className="hm-qf-emoji">⭐</span>4.0+
              </button>
              <button type="button" className={`hm-qf-chip ${maxDistance === 3000 ? 'hm-qf-chip--active' : ''}`} onClick={() => setMaxDistance((v) => (v === 3000 ? null : 3000))}>
                <span className="hm-qf-emoji">📍</span>Within 3 km
              </button>
            </div>
          </div>

          {/* Active chips from the Filters modal — visible + removable inline, no need to reopen the sheet */}
          {hasAnyActiveChip && (
            <div className="hm-active-chips">
              {vegMode && (
                <span className="hm-chip hm-chip--green">
                  🥗 Veg only
                  <button onClick={() => { setVegMode(false); setActiveQF(null); }}><X size={12} /></button>
                </span>
              )}
              {isOpen && (
                <span className="hm-chip hm-chip--rust">
                  🟢 Open now
                  <button onClick={() => setIsOpen(false)}><X size={12} /></button>
                </span>
              )}
              {minRating != null && (
                <span className="hm-chip hm-chip--amber">
                  ⭐ {minRating}+
                  <button onClick={() => setMinRating(null)}><X size={12} /></button>
                </span>
              )}
              {maxDistance != null && (
                <span className="hm-chip hm-chip--rust">
                  📍 &lt; {(maxDistance / 1000).toFixed(0)} km
                  <button onClick={() => setMaxDistance(null)}><X size={12} /></button>
                </span>
              )}
              {activeAmbience.map(val => {
                const af = AMBIENCE_FILTERS.find(a => a.val === val);
                return af ? (
                  <span key={val} className="hm-chip hm-chip--amber">
                    {af.emoji} {af.label}
                    <button onClick={() => removeAmbience(val)}><X size={12} /></button>
                  </span>
                ) : null;
              })}
              {activeVibes.map(val => {
                const vf = VIBE_FILTERS.find(v => v.val === val);
                return vf ? (
                  <span key={val} className="hm-chip hm-chip--violet">
                    {vf.emoji} {vf.label}
                    <button onClick={() => removeVibe(val)}><X size={12} /></button>
                  </span>
                ) : null;
              })}
              {activeCuisine.map(val => {
                const cf = CUISINE_CATS.find(c => c.val === val);
                return cf ? (
                  <span key={val} className="hm-chip hm-chip--rust">
                    {cf.emoji} {cf.label}
                    <button onClick={() => removeCuisine(val)}><X size={12} /></button>
                  </span>
                ) : null;
              })}
              {activeOccasion.map(val => {
                const of = OCCASION_FILTERS.find(o => o.val === val);
                return of ? (
                  <span key={val} className="hm-chip hm-chip--violet">
                    {of.emoji} {of.label}
                    <button onClick={() => removeOccasion(val)}><X size={12} /></button>
                  </span>
                ) : null;
              })}
              <button className="hm-chip hm-chip--clear" onClick={clearAllFilters}>Clear all</button>
            </div>
          )}

          {/* ───── RESTAURANTS ───── */}
          <div className="hm-section" style={{ paddingTop: 20 }}>
            <div className="hm-results-header">
              <MapPin size={14} />
              <span>Results for <strong>{address || 'Current Location'}</strong></span>
              {activeFilterCount > 0 && (
                <span className="hm-filter-active-note">
                  {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''} active
                </span>
              )}
            </div>

            {!isLoading && !searchError && (q || searchInfo?.notice) && (
              <div className="sr-context">
                {!!q && (
                  <div className="sr-chips">
                    {searchInfo?.query?.text ? (
                      <span className="sr-chip sr-chip--query">
                        “{searchInfo.query.text}”
                        <button type="button" aria-label="Remove search text" onClick={() => removePhrase(searchInfo.query.text)}><X size={14} /></button>
                      </span>
                    ) : !(searchInfo?.query?.interpreted || []).length && (
                      <span className="sr-chip sr-chip--query">
                        “{q}”
                        <button type="button" aria-label="Clear search" onClick={() => setQ('')}><X size={14} /></button>
                      </span>
                    )}
                    {(searchInfo?.query?.interpreted || []).map((c) => (
                      <span key={`${c.type}-${c.phrase}`} className="sr-chip">
                        {c.emoji && <span aria-hidden="true">{c.emoji}</span>}{c.label}
                        <button type="button" aria-label={`Remove ${c.label}`} onClick={() => removePhrase(c.phrase)}><X size={14} /></button>
                      </span>
                    ))}
                  </div>
                )}

                {searchInfo?.notice && (() => {
                  const kind = searchInfo.mode === 'related' || searchInfo.mode === 'popular' ? searchInfo.mode : 'relaxed';
                  const Icon = kind === 'related' ? Sparkles : kind === 'popular' ? Compass : Info;
                  return (
                    <div className={`sr-notice sr-notice--${kind}`} role="status">
                      <Icon size={18} className="sr-notice-icon" />
                      <div className="sr-notice-body">
                        <span>{searchInfo.notice}</span>
                        {searchInfo.didYouMean?.length > 0 && (
                          <div className="sr-dym">
                            Did you mean
                            {searchInfo.didYouMean.map((s) => <button key={s} type="button" onClick={() => setQ(s)}>{s}</button>)}
                          </div>
                        )}
                        {activeFilterCount > 0 && searchInfo.mode !== 'match' && (
                          <button type="button" className="sr-text-btn" onClick={clearAllFilters}>Clear filters</button>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {!isLoading && !searchError && nearestOutlets.length > 0 && (
              <div className="sr-heading">
                {searchInfo?.mode === 'popular' ? 'Popular near you'
                  : searchInfo?.mode === 'related' ? 'You might like'
                    : q ? `${searchInfo?.total ?? nearestOutlets.length} place${(searchInfo?.total ?? nearestOutlets.length) === 1 ? '' : 's'} for “${q}”`
                      : 'Restaurants near you'}
              </div>
            )}
          </div>

          {locationLoading && !coordinates.lat ? (
            <div className="hm-loading"><div className="hm-loading-dots"><span /><span /><span /></div><p>Detecting your location…</p></div>
          ) : isLoading ? (
            <div className="hm-loading"><div className="hm-loading-dots"><span /><span /><span /></div><p>{q ? `Searching “${q}”…` : 'Finding the best spots near you…'}</p></div>
          ) : searchError ? (
            <div className="sr-error" role="alert">
              <div className="hm-no-results-icon">⚠️</div>
              <p>{searchError}</p>
              <button type="button" onClick={() => findNearestOutlets(true)}>Try again</button>
            </div>
          ) : nearestOutlets.length === 0 ? (
            <div className="hm-no-results">
              <div className="hm-no-results-icon">🍽️</div>
              <h3>No restaurants here yet</h3>
              <p>{searchInfo?.notice || 'We’re adding new places every day — try another area.'}</p>
            </div>
          ) : (
            <div className="hm-section" style={{ paddingTop: 0 }}>
              <OutletLets nearestOutlets={nearestOutlets} setIsSingleItem={setIsSingleItem} fetchMore={fetchMore} hasMore={hasMore} />
            </div>
          )}

          <div className="hm-view-all-wrap">
            <button className="hm-view-all-btn" onClick={fetchMore} disabled={!hasMore}>
              {hasMore ? 'View All Restaurants →' : 'All results loaded'}
            </button>
          </div>

          {/* ───── DISCOVER MORE — browse by vibe / cuisine / occasion, kept below results so it never blocks them ───── */}
          <div className="hm-section" style={{ paddingTop: 40 }}>
            <div className="hm-section-title">✨ Discover More Ways to Explore</div>
            <p className="hm-section-sub">Vibes, cuisines &amp; occasions — open Filters to browse and apply any of these</p>
            <div className="hm-discover-row">
              <button className="hm-discover-card" onClick={() => setShowFilterSheet(true)}>
                <span className="hm-discover-emoji">🏕️</span>
                <span className="hm-discover-label">Café Vibes</span>
              </button>
              <button className="hm-discover-card" onClick={() => setShowFilterSheet(true)}>
                <span className="hm-discover-emoji">🌍</span>
                <span className="hm-discover-label">Cultural Themes</span>
              </button>
              <button className="hm-discover-card" onClick={() => setShowFilterSheet(true)}>
                <span className="hm-discover-emoji">🍜</span>
                <span className="hm-discover-label">Cuisines</span>
              </button>
              <button className="hm-discover-card" onClick={() => setShowFilterSheet(true)}>
                <span className="hm-discover-emoji">🎉</span>
                <span className="hm-discover-label">Occasions</span>
              </button>
            </div>
          </div>

          {/* ───── HAPPY MOMENTS SECTION ───── */}
          <div className="hm-section" style={{ paddingTop: 40 }}>
            <div className="hm-section-header">
              <div className="hm-section-title">✨ Happy Moments</div>
              <Link to="/happymoments" className="hm-section-link">See all →</Link>
            </div>
            <p className="hm-section-sub">Real food, real smiles — shared by our community</p>
          </div>

          {momentsLoading ? (
            <MomentsSkeleton />
          ) : momentsError ? (
            <div className="hm-moments-scroll">
              <div className="hm-blogs-error">
                <span>{momentsError}</span>
                <button onClick={fetchHomeMoments}>Retry</button>
              </div>
            </div>
          ) : homeMoments.length === 0 ? (
            <div className="hm-moments-scroll">
              <p className="hm-blogs-empty">No moments yet. <Link to="/happymoments">Be the first to share!</Link></p>
            </div>
          ) : (
            <div className="hm-moments-scroll">
              {homeMoments.map((moment, i) => (
                <MomentCard key={moment._id ?? i} moment={moment} />
              ))}
              <Link to="/happymoments" className="hm-moment-card hm-moment-card--see-all">
                <div className="hm-moment-see-all-inner">
                  <div className="hm-moment-see-all-icon">✨</div>
                  <div className="hm-moment-see-all-text">See All<br />Moments</div>
                </div>
              </Link>
            </div>
          )}

          {/* ───── BLOGS SECTION ───── */}
          <div className="hm-section" style={{ paddingTop: 36 }}>
            <div className="hm-section-header">
              <div className="hm-section-title">📰 From Our Blog</div>
              <Link to="/blog" className="hm-section-link">See all →</Link>
            </div>
            <p className="hm-section-sub">Stories, guides &amp; food discoveries</p>
          </div>

          {blogsLoading ? (
            <BlogSkeleton />
          ) : blogsError ? (
            <div className="hm-blogs-scroll">
              <div className="hm-blogs-error">
                <span>{blogsError}</span>
                <button onClick={fetchHomeBlogs}>Retry</button>
              </div>
            </div>
          ) : homeBlogs.length === 0 ? (
            <div className="hm-blogs-scroll">
              <p className="hm-blogs-empty">No posts yet. <Link to="/write">Be the first to write one!</Link></p>
            </div>
          ) : (
            <div className="hm-blogs-scroll">
              {homeBlogs.map((blog, i) => (
                <BlogCard key={blog._id ?? i} blog={blog} />
              ))}
            </div>
          )}

          {/* ───── FOOTER ───── */}
          <footer className="hm-footer">
  <div className="hm-footer-logo">
    <img
      src="/production_logo/RestroPilots_Primary_Logo.svg"
      alt="RestroPilots"
    />
  </div>
  <div className="hm-footer-links">
    <Link to="/partner">Partner with Us</Link>
  </div>
  <div className="hm-footer-copy">© {new Date().getFullYear()} RestroPilots. All rights reserved.</div>
</footer>

          <div className="hm-page-spacer" />
        </div>
      ) : (
        <CafePage setIsSingleItem={setIsSingleItem} />
      )}

      <BottomNav />

      {/* ───── ONE-CLICK FILTER MODAL — replaces the old buried accordion + 4 separate on-page sections ───── */}
      <FilterSheet
        open={showFilterSheet}
        onClose={() => setShowFilterSheet(false)}
        vegMode={vegMode} setVegMode={setVegMode}
        isOpen={isOpen} setIsOpen={setIsOpen}
        activeAmbience={activeAmbience} toggleAmbience={toggleAmbience}
        activeVibes={activeVibes} toggleVibe={toggleVibe}
        activeCuisine={activeCuisine} toggleCuisine={toggleCuisine}
        activeOccasion={activeOccasion} toggleOccasion={toggleOccasion}
        minRating={minRating} setMinRating={setMinRating}
        maxDistance={maxDistance} setMaxDistance={setMaxDistance}
        activeFilterCount={activeFilterCount}
        onClearAll={clearAllFilters}
      />

      {/* ── Choose location (opened from "Restaurants near …") ── */}
      {showLocationSheet && (
        <>
          <div className="hm-loc-overlay" onClick={() => setShowLocationSheet(false)} />
          <div className="hm-loc-sheet" role="dialog" aria-modal="true" aria-label="Choose location">
            <div className="hm-loc-sheet-head">
              <strong>Choose location</strong>
              <button type="button" className="hm-loc-close" onClick={() => setShowLocationSheet(false)} aria-label="Close"><X size={18} /></button>
            </div>
            <button
              type="button"
              className="hm-loc-gps"
              onClick={() => { setAddress(''); fetchUserLocation(true); setShowLocationSheet(false); }}
              disabled={locationLoading}
            >
              <Navigation size={16} /> Use my current location
            </button>
            <div className="hm-loc-or">or search an area</div>
            <PlacesAutocomplete value={address} onChange={setAddress} onSelect={handleSelect}>
              {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                <div className="hm-loc-search">
                  <div className="hm-loc-input-wrap">
                    {loading ? <Loader2 size={16} className="hm-spin" /> : <Search size={16} />}
                    <input
                      autoFocus
                      {...getInputProps({ placeholder: `e.g. ${typingPlaceholder || 'Saket'}`, className: 'hm-loc-input' })}
                    />
                  </div>
                  {suggestions.length > 0 && (
                    <div className="hm-loc-results">
                      {suggestions.map(s => (
                        <div key={s.placeId}
                          {...getSuggestionItemProps(s, { className: `hm-loc-result ${s.active ? 'hm-loc-result--active' : ''}` })}
                        >
                          <MapPin size={14} />
                          <span>{s.description}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </PlacesAutocomplete>
          </div>
        </>
      )}

      {/* ── Location Permission Popup ── */}
      {showLocationPopup && (
        <>
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000 }}
            onClick={() => setShowLocationPopup(false)}
          />
          <div style={{
            position: 'fixed', bottom: 0, left: 0, right: 0,
            background: '#fff', borderRadius: '24px 24px 0 0',
            padding: '28px 24px 44px', zIndex: 1001, textAlign: 'center',
            boxShadow: '0 -4px 32px rgba(0,0,0,0.15)',
          }}>
            <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>📍</div>
            <div style={{ fontWeight: 700, fontSize: '1.1rem', marginBottom: 8 }}>
              Enable Location Access
            </div>
            <p style={{ color: '#8c7d6e', fontSize: '0.88rem', lineHeight: 1.6, marginBottom: 20 }}>
              We need your location to show restaurants near you. Please allow location access in your browser settings.
            </p>
            <div style={{ background: '#faf3e8', borderRadius: 12, padding: '12px 16px', marginBottom: 20, textAlign: 'left' }}>
              <p style={{ fontSize: '0.8rem', color: '#6b4f2a', fontWeight: 600, marginBottom: 4 }}>How to enable:</p>
              <p style={{ fontSize: '0.78rem', color: '#8c7d6e', lineHeight: 1.6 }}>
                🔒 Tap the <strong>lock icon</strong> in your browser's address bar → <strong>Site settings</strong> → Set <strong>Location</strong> to <strong>Allow</strong>, then reload the page.
              </p>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setShowLocationPopup(false)}
                style={{
                  flex: 1, padding: '13px', borderRadius: 12,
                  border: '1.5px solid #ede9e2', background: '#fff',
                  fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer', color: '#4a3f36',
                }}
              >
                Search Manually
              </button>
              <button
                onClick={() => { setShowLocationPopup(false); fetchUserLocation(true); }}
                style={{
                  flex: 1, padding: '13px', borderRadius: 12,
                  border: 'none', background: '#b83a20',
                  fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer', color: '#fff',
                  boxShadow: '0 4px 14px rgba(184,58,32,0.28)',
                }}
              >
                Try Again
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── First-visit intro — how FoodFinder removes the wait.
           Yields to the location-permission sheet so the two never
           stack; it reappears once that one is dismissed. Skipped
           on the single-outlet view, which isn't the search flow
           the intro is pitching. ── */}
      <WelcomeModal
        open={showWelcome && !showLocationPopup && !isSingleItem}
        onClose={() => setShowWelcome(false)}
        onPrimary={focusSearch}
      />
    </div>
  );
};

export default Home;